import { getServices } from "../../../api/getServices";
import { appendTerminalError } from "../actions/terminal";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { KernelSession, LocalState } from "../types/LocalState";
import { LocalActionKey } from "./ActionKeys";

/** An action that sets the local state collectively. */
export interface SetLocalStateAction
  extends AppAction<LocalActionKey.SET_LOCAL_STATE, { local: LocalState }> {}

/** All the local actions. */
export type LocalAction = SetLocalStateAction;

/** Sets the local state. */
export const setLocalState = (local: LocalState): SetLocalStateAction =>
  getStore().dispatch({
    type: LocalActionKey.SET_LOCAL_STATE,
    payload: { local },
  });

/** Prevents fetching sessions in parallel. */
let sessionsInFlight = false;

/** Update the local state of sessions from the server. */
export const updateLocalSessions = async () => {
  const services = getServices();
  // If there is currently a sessions request in flight, don't fetch.
  if (sessionsInFlight) {
    return;
  }
  try {
    sessionsInFlight = true;
    const {
      quotaLimits,
      sessions,
    } = await services.kernelsClient.listKernelSessions({});
    const local = { quotaLimits, sessions };

    // Update Redux state.
    setLocalState(local);

    // Update local storage to update this state on other tabs.
    services.kernelStorageClient.writeLocalState(local);
  } catch (error) {
    appendTerminalError("Unable to update local sessions state.", error);
  } finally {
    sessionsInFlight = false;
  }
};

/** Cancels a session and updates the local state. */
export const cancelSession = async (kernelRunId: number) => {
  await getServices().kernelsClient.cancelKernelRun({ kernelRunId });
  await updateLocalSessions();
};

type SessionFilter = (session: KernelSession) => boolean;

/** Cancel all the interactive sessions. Filterable with optional argument. */
export const cancelAllInteractiveSessions = async (
  sessionFilter: SessionFilter = () => true
) => {
  const services = getServices();
  const { sessions } = await services.kernelsClient.listKernelSessions({});
  const tasks = sessions
    .filter(sessionFilter)
    .map(({ kernelRunId }) =>
      services.kernelsClient.cancelKernelRun({ kernelRunId })
    );
  await Promise.all(tasks);
  await updateLocalSessions();
};
